import {cn} from '@/lib/utils';
import ArrowRightIcon from '../Icons/ArrowRight';
import {IDatePickerDictonary} from '@/dictionaries/components/datePicker/types';
import {countRangeDays} from '@/utils/village';

type Props = {
  rangeValue: {start: Date | null; end: Date | null};
  lang: string;
  dict: IDatePickerDictonary;
  showSummary?: boolean;
  isOneDate?: boolean;
};

export default function DatePickerHeader({
  rangeValue,
  lang,
  dict,
  showSummary,
  isOneDate,
}: Props) {
  const rangeIsValid = rangeValue.start && rangeValue.end;
  const nights = countRangeDays(rangeValue);

  const formatRangeDateLabel = (
    label: string,
    hideWeekDay: boolean = false,
    lang: string,
  ) => {
    if (lang.includes('en')) {
      const splitedLabel = label.split(',');
      if (hideWeekDay) {
        const year = splitedLabel[1];
        const month = splitedLabel[0].substring(0, 3);
        const day = splitedLabel[0].substring(4, 6);
        return `${day} ${month} ${year}`;
      }

      const weekDay = splitedLabel[0];
      const day = splitedLabel[1].substring(5, 7);
      const month = splitedLabel[1].substring(0, 4);
      const year = splitedLabel[2];
      return `${weekDay} ${day} ${month} ${year}`;
    }

    if (lang.includes('de')) {
      if (hideWeekDay) {
        const splitedLabel = label.split('.');
        const day = splitedLabel[0];
        const month = splitedLabel[1];
        const year = splitedLabel[2];
        return `${day} ${month} ${year}`;
      }

      const splitedLabel = label.split(',');
      const weekDay = splitedLabel[0];
      const day = splitedLabel[1].substring(1, 3);
      const monthAndYear = splitedLabel[1].substring(4);
      return `${weekDay} ${day} ${monthAndYear}`;
    }
    return label;
  };

  const getRangeDateLabel = (
    rangeDate: Date | null,
    hideWeekDay: boolean = false,
  ) => {
    if (rangeDate) {
      let formaterLang = lang;
      const langs = ['en', 'it', 'fr', 'de'];
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      };

      if (hideWeekDay) {
        delete options.weekday;
      }
      if (!langs.includes(lang)) {
        formaterLang = 'en';
      }

      const dateFormatter = new Intl.DateTimeFormat(formaterLang, options);
      const label = dateFormatter.format(rangeDate);

      return formatRangeDateLabel(label, hideWeekDay, formaterLang);
    }
  };

  return (
    <div className="w-full flex flex-col desktop:flex-row desktop:items-center desktop:justify-around bg-gray-100 md:p-5">
      {showSummary ? (
        <div
          className={cn('flex flex-col', {
            'desktop:items-start': rangeIsValid,
          })}>
          <h4 className="text-center text-subtitle font-title font-bold py-5 md:pb-5 desktop:p-0 md:pt-0">
            {rangeIsValid ? `${nights} ${dict.nights}` : dict.title}
          </h4>
          {rangeIsValid && (
            <small className="hidden desktop:block">{`
            ${getRangeDateLabel(rangeValue.start, true)} - ${getRangeDateLabel(
              rangeValue.end,
              true,
            )}`}</small>
          )}
        </div>
      ) : (
        <h4
          className={cn(
            'text-center text-subtitle font-title font-bold desktop:p-0 md:pt-0',
            {
              'py-5 md:pb-5': !isOneDate,
            },
            {
              'py-4 md:py-0': isOneDate,
            },
          )}>
          {dict.title}
        </h4>
      )}
      {!isOneDate && (
        <div className="w-full md:w-[390px] md:mx-auto desktop:m-0 h-[2.5625rem] min-h-[44px] px-2 py-4 text-gray-700 bg-white border-y md:border border-underline flex justify-around items-center">
          <span
            className={cn('text-graphite text-base leading-[1.5]', {
              'underline-effect': !rangeValue.start,
            })}>
            {rangeValue.start
              ? getRangeDateLabel(rangeValue.start)
              : dict.checkIn}
          </span>
          <ArrowRightIcon className="fill-current text-graphite w-7 h-7" />
          <span
            className={cn('text-gray-700 text-base leading-[1.5]', {
              'underline-effect': !rangeValue.end && rangeValue.start,
              'text-graphite': rangeValue.start,
            })}>
            {rangeValue.end ? getRangeDateLabel(rangeValue.end) : dict.checkOut}
          </span>
        </div>
      )}
    </div>
  );
}
